"use client";

import { Settings } from "lucide-react";
import { useCallback, useState } from "react";
import { SecureExportModal } from "@/components/dashboard/forms-management/modals/secure-export-modal";

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "@/hooks/use-toast";
import { useTranslations } from "next-intl";
import { formsDb, type Form } from "@/lib/database";
import {
	FormSettingsDesktopLayout,
	FormSettingsMobileLayout,
} from "./components";
import { useFormSettings } from "./hooks";
import type { FormSettingsModalProps, FormSettingsSection } from "./types";

export function FormSettingsModal({
	isOpen,
	onClose,
	schema,
	onSchemaUpdate,
	userEmail,
	formId,
}: FormSettingsModalProps) {

	const t = useTranslations("formSettings");	const {
		localSettings,
		updateSettings,
		updateRateLimit,
		updateDuplicatePrevention,
		updateProfanityFilter,
		updateBotProtection,
		updateResponseLimit,
		updatePasswordProtection,
		updateSocialMedia,
		updateNotifications,
		updateApi,
		resetSettings,
	} = useFormSettings(schema, userEmail);

	const [activeSection, setActiveSection] =
		useState<FormSettingsSection>("basic");
	const [showSecureExportModal, setShowSecureExportModal] = useState(false);
	const [secureExportForm, setSecureExportForm] = useState<Form | null>(null);
	const [loadingExportForm, setLoadingExportForm] = useState(false);
	const { user } = useAuth();

	const handleOpenSecureExport = useCallback(async () => {
		if (!formId) {
			toast.error(t("saveFirstToExport"));
			return;
		}

		setLoadingExportForm(true);
		try {
			const form = await formsDb.getForm(formId, user?.id ?? "anonymous");
			setSecureExportForm(form);
			setShowSecureExportModal(true);
		} catch (error) {
			console.error("Failed to prepare secure export:", error);
			toast.error(t("failedPrepareExport"));
		} finally {
			setLoadingExportForm(false);
		}
	}, [formId, user]);

	const sectionProps = {
		localSettings,
		updateSettings,
		updateRateLimit,
		updateDuplicatePrevention,
		updateProfanityFilter,
		updateBotProtection,
		updateResponseLimit,
		updatePasswordProtection,
		updateSocialMedia,
		updateNotifications,
		updateApi,
		formId,
		schema,
		onSchemaUpdate,
	};

	return (
		<>
			<Dialog onOpenChange={onClose} open={isOpen}>
				<DialogContent className="b-5 flex h-[90dvh] w-full grow flex-col gap-0 overflow-hidden p-0 sm:max-w-6xl">
					<DialogHeader className="flex shrink-0 flex-row items-center p-4">
						<div className="flex items-center gap-3">
							<Settings className="size-5 text-primary" />
							<DialogTitle className="font-semibold text-xl">
								{t("formSettingsTitle")}
							</DialogTitle>
						</div>
					</DialogHeader>
					<div className="mb-12 flex min-h-0 flex-1 flex-col p-4 md:mb-0">
						{formId || (onSchemaUpdate && onSchemaUpdate !== undefined) ? (
							<>
								<FormSettingsDesktopLayout
									activeSection={activeSection}
									exportDisabled={!formId || loadingExportForm}
									exportLoading={loadingExportForm}
									onClose={onClose}
									onExportClick={handleOpenSecureExport}
									onSectionChange={setActiveSection}
									sectionProps={sectionProps}
								/>
								<FormSettingsMobileLayout
									activeSection={activeSection}
									exportDisabled={!formId || loadingExportForm}
									exportLoading={loadingExportForm}
									onClose={onClose}
									onExportClick={handleOpenSecureExport}
									onSectionChange={setActiveSection}
									sectionProps={sectionProps}
								/>
							</>
						) : (
							<div className="flex h-full flex-col items-center justify-center gap-4 text-center">
								<p className="text-muted-foreground">
									{t("saveFormFirst")}
								</p>
							</div>
						)}
					</div>
				</DialogContent>
			</Dialog>

			<SecureExportModal
				form={secureExportForm}
				isOpen={showSecureExportModal}
				onClose={() => {
					setShowSecureExportModal(false);
					setSecureExportForm(null);
				}}
			/>
		</>
	);
}
